import React from 'react';
import '@/App.css';
import '@/i18n'; // initialize i18next once at app start
import { BrowserRouter, Navigate, Route, Routes } from 'react-router-dom';
import { registerServiceWorker, isStandalone } from '@/lib/pwaRegister';
import { crewHomeCode } from '@/lib/reminders';

// Register the service worker + install-prompt listeners on first app boot.
// Safe no-op in dev; real registration happens only on production builds.
registerServiceWorker();
import { Toaster } from '@/components/ui/sonner';

import { AuthProvider, useAuth } from '@/context/AuthContext';
import { ThemeProvider, ForceDark, ForcePortalLight, useTheme } from '@/context/ThemeContext';
import ProtectedRoute from '@/components/ProtectedRoute';
import PublicOnlyRoute from '@/components/PublicOnlyRoute';
import RoleRoute from '@/components/RoleRoute';

import LoginPage from '@/pages/LoginPage';
import SignupPage from '@/pages/SignupPage';
import OnboardingPage from '@/pages/OnboardingPage';
import ForgotPasswordPage from '@/pages/ForgotPasswordPage';
import ResetPasswordPage from '@/pages/ResetPasswordPage';
import DashboardLayout from '@/components/dashboard/DashboardLayout';

import DashboardHomePage from '@/pages/modules/DashboardHomePage';
import ClientsLeadsPage from '@/pages/modules/ClientsLeadsPage';
import ActiveJobsPage from '@/pages/modules/ActiveJobsPage';
import InvoicesPage from '@/pages/modules/InvoicesPage';
import CrewClockPage from '@/pages/modules/CrewClockPage';
import EstimatesPage from '@/pages/modules/EstimatesPage';
import JobCostingPage from '@/pages/modules/JobCostingPage';
import JobFilesPage from '@/pages/modules/JobFilesPage';
import ReceiptScannerPage from '@/pages/modules/ReceiptScannerPage';
import RebateTrackerPage from '@/pages/modules/RebateTrackerPage';
import SchedulePage from '@/pages/modules/SchedulePage';
import RemindersPage from '@/pages/modules/RemindersPage';
import PaymentsPage from '@/pages/modules/PaymentsPage';
import SettingsPage from '@/pages/modules/SettingsPage';
import ClientPortalPage from '@/pages/modules/ClientPortalPage';
import CrewPortalPage from '@/pages/modules/CrewPortalPage';
import PQIIntelligencePage from '@/pages/modules/PQIIntelligencePage';
import ApprovalsPage from '@/pages/modules/ApprovalsPage';
import MilliConsolePage from '@/pages/milli/MilliConsolePage';
import BrandLibraryPage from '@/pages/modules/BrandLibraryPage';
import DaySummaryPage from '@/pages/modules/DaySummaryPage';
import PQVCapturePage from '@/pages/modules/PQVCapturePage';
import ClientPortalView from '@/pages/portal/ClientPortalView';
import CrewPortalView from '@/pages/portal/CrewPortalView';

/**
 * RoleAwareDashboardHome
 *
 * Phase 21: the dashboard root (`/dashboard`) renders the financial
 * "Command Center" home for admins, but a non-admin (crew_lead) must never
 * see it. Instead we redirect them to Crew Clock \u2014 the one module they
 * legitimately operate. We do this here as a route component so it sits
 * inside the same DashboardLayout and avoids a full-page flicker.
 */
// The installed app starts at "/". A crew member (no login) who installed it
// from their crew portal goes back to that portal instead of the sign-in page.
const RootRedirect = () => {
  const { user, loading } = useAuth();
  if (loading) return null;
  const crew = !user && isStandalone() ? crewHomeCode() : null;
  return <Navigate to={crew ? `/portal/crew/${crew}` : '/dashboard'} replace />;
};

const RoleAwareDashboardHome = () => {
  const { loading, profileLoading, isAdmin } = useAuth();
  if (loading || profileLoading) {
    // ProtectedRoute already shows a spinner during the initial gate; this
    // is a no-op placeholder until the role is resolved.
    return null;
  }
  if (!isAdmin) {
    return <Navigate to="/dashboard/crew-clock" replace />;
  }
  return <DashboardHomePage />;
};

// Redirect a signed-in contractor (admin) who hasn't finished onboarding into
// the wizard before they can reach the dashboard. Crew_leads pass straight
// through (needsOnboarding is false for them).
const OnboardingGate = ({ children }) => {
  const { needsOnboarding, loading, profileLoading } = useAuth();
  if (loading || profileLoading) return null;
  if (needsOnboarding) return <Navigate to="/onboarding" replace />;
  return children;
};

// The signed-in workspace follows the user's theme; the "front door"
// (login, password reset, and the client/crew portals) stays branded dark
// regardless, so the gold-on-black first impression is preserved.
const ThemedToaster = () => {
  const { resolved } = useTheme();
  return (
    <Toaster
      theme={resolved}
      position="top-right"
      toastOptions={{
        style: {
          background: 'hsl(var(--card))',
          color: 'hsl(var(--foreground))',
          border: '1px solid hsl(var(--border))',
        },
      }}
    />
  );
};

function App() {
  return (
    <div className="App">
      <ThemeProvider>
      <AuthProvider>
        <BrowserRouter>
          <Routes>
            <Route
              path="/login"
              element={
                <PublicOnlyRoute>
                  <ForceDark>
                    <LoginPage />
                  </ForceDark>
                </PublicOnlyRoute>
              }
            />

            {/* Self-serve contractor signup (front door — branded dark) */}
            <Route
              path="/signup"
              element={
                <PublicOnlyRoute>
                  <ForceDark>
                    <SignupPage />
                  </ForceDark>
                </PublicOnlyRoute>
              }
            />

            {/* Onboarding wizard — authenticated, pre-dashboard, branded dark */}
            <Route
              path="/onboarding"
              element={
                <ProtectedRoute>
                  <ForceDark>
                    <OnboardingPage />
                  </ForceDark>
                </ProtectedRoute>
              }
            />

            {/* Password reset flow.
                - /forgot-password: only when signed-out (PublicOnlyRoute).
                - /reset-password: NOT gated by PublicOnlyRoute because
                  Supabase materializes a temporary recovery session from
                  the URL fragment — PublicOnlyRoute would bounce them to
                  /dashboard before they can set their new password. */}
            <Route
              path="/forgot-password"
              element={
                <PublicOnlyRoute>
                  <ForceDark>
                    <ForgotPasswordPage />
                  </ForceDark>
                </PublicOnlyRoute>
              }
            />
            <Route
              path="/reset-password"
              element={
                <ForceDark>
                  <ResetPasswordPage />
                </ForceDark>
              }
            />

            {/* Public portal routes \u2014 accessed via short code, no Supabase Auth required from the user */}
            {/* Client portal reads light for every homeowner (dark header bar
                only); the crew portal stays branded dark. */}
            <Route path="/portal/client" element={<ForcePortalLight><ClientPortalView /></ForcePortalLight>} />
            <Route path="/portal/client/:code" element={<ForcePortalLight><ClientPortalView /></ForcePortalLight>} />
            <Route path="/portal/crew" element={<ForceDark><CrewPortalView /></ForceDark>} />
            <Route path="/portal/crew/:code" element={<ForceDark><CrewPortalView /></ForceDark>} />

            {/* Milli's console — her own front door, full screen, outside
                the dashboard chrome. Same login, owner-only, always dark. */}
            <Route
              path="/milli"
              element={
                <ProtectedRoute>
                  <OnboardingGate>
                    <RoleRoute role="admin">
                      <ForceDark>
                        <MilliConsolePage />
                      </ForceDark>
                    </RoleRoute>
                  </OnboardingGate>
                </ProtectedRoute>
              }
            />

            <Route
              path="/dashboard"
              element={
                <ProtectedRoute>
                  <OnboardingGate>
                    <DashboardLayout />
                  </OnboardingGate>
                </ProtectedRoute>
              }
            >
              {/* Smart home: admin sees Command Center, crew_lead is sent to Crew Clock */}
              <Route index element={<RoleAwareDashboardHome />} />

              {/* Admin-only modules (financial + management) */}
              <Route
                path="clients-leads"
                element={<RoleRoute role="admin"><ClientsLeadsPage /></RoleRoute>}
              />
              <Route
                path="active-jobs"
                element={<RoleRoute role="admin"><ActiveJobsPage /></RoleRoute>}
              />
              <Route
                path="invoices"
                element={<RoleRoute role="admin"><InvoicesPage /></RoleRoute>}
              />
              <Route
                path="estimates"
                element={<RoleRoute role="admin"><EstimatesPage /></RoleRoute>}
              />
              <Route
                path="job-costing"
                element={<RoleRoute role="admin"><JobCostingPage /></RoleRoute>}
              />
              <Route
                path="receipt-scanner"
                element={<RoleRoute role="admin"><ReceiptScannerPage /></RoleRoute>}
              />
              <Route
                path="rebate-tracker"
                element={<RoleRoute role="admin"><RebateTrackerPage /></RoleRoute>}
              />
              <Route
                path="reminders"
                element={<RoleRoute role="admin"><RemindersPage /></RoleRoute>}
              />
              <Route
                path="payments"
                element={<RoleRoute role="admin"><PaymentsPage /></RoleRoute>}
              />
              <Route
                path="settings"
                element={<RoleRoute role="admin"><SettingsPage /></RoleRoute>}
              />
              <Route
                path="client-portal"
                element={<RoleRoute role="admin"><ClientPortalPage /></RoleRoute>}
              />
              <Route
                path="crew-portal"
                element={<RoleRoute role="admin"><CrewPortalPage /></RoleRoute>}
              />
              <Route
                path="pq-intelligence"
                element={<RoleRoute role="admin"><PQIIntelligencePage /></RoleRoute>}
              />
              {/* K.O. Command approval inbox — the reserved route from
                  CLAUDE.md. Decisions Milli escalates for human sign-off. */}
              <Route
                path="approvals"
                element={<RoleRoute role="admin"><ApprovalsPage /></RoleRoute>}
              />
              {/* The old cockpit address: home-screen installs and bookmarks
                  from before the console keep working. */}
              <Route path="milli" element={<Navigate to="/milli" replace />} />
              {/* End-of-day owner summary (117) — the end-of-day phone
                  notification opens here. Owner only. */}
              <Route
                path="day-summary"
                element={<RoleRoute role="admin"><DaySummaryPage /></RoleRoute>}
              />
              {/* K.O. Command brand library (069): registered brand assets
                  and the design department's queue. Read-only. */}
              <Route
                path="brand"
                element={<RoleRoute role="admin"><BrandLibraryPage /></RoleRoute>}
              />
              {/* PQ Vision walkthrough capture — admin-only. Lives under the
                  same role gate as PQ Intelligence; no separate sidebar item
                  is exposed (per spec). Accessed via the "Start Walkthrough"
                  button on a job, or the Vision tab on the PQ Intelligence
                  page (read-only list). */}
              <Route
                path="pq-vision/walkthrough/:id"
                element={<RoleRoute role="admin"><PQVCapturePage /></RoleRoute>}
              />

              {/* Operational modules: crew_lead AND admin can access */}
              <Route path="crew-clock" element={<CrewClockPage />} />
              <Route path="job-files" element={<JobFilesPage />} />
              <Route path="schedule" element={<SchedulePage />} />
            </Route>

            <Route path="/" element={<RootRedirect />} />
            <Route path="*" element={<Navigate to="/dashboard" replace />} />
          </Routes>
          <ThemedToaster />
        </BrowserRouter>
      </AuthProvider>
      </ThemeProvider>
    </div>
  );
}

export default App;
